import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  Banknote,
  Building2,
  CheckCircle2,
  Clock,
  CreditCard,
  DollarSign,
  Plus,
  TrendingDown,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui';
import { useOutstanding, usePaymentHistory, useRecordPayment } from '@/hooks/use-payments';
import { usePermission } from '@/hooks/use-permissions';

function fmt(v: number): string {
  return v.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });
}

const PAYMENT_METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer', icon: Building2 },
  { value: 'CASH', label: 'Cash', icon: Banknote },
  { value: 'CHECK', label: 'Check', icon: DollarSign },
  { value: 'CARD', label: 'Card', icon: CreditCard },
  { value: 'DIRECT_DEBIT', label: 'Direct Debit', icon: TrendingDown },
];

function getMutationErrorMessage(error: unknown) {
  if (isAxiosError(error)) {
    const message = error.response?.data?.message ?? error.response?.data?.error?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return 'Failed to record payment';
}

export function PaymentsTab({ loanId }: { loanId: string }) {
  const { data: payments, isLoading: loadingHistory } = usePaymentHistory(loanId);
  const { data: outstanding } = useOutstanding(loanId);
  const recordPayment = useRecordPayment();
  const canRecordPayment = usePermission('payment.record');

  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return;

    try {
      await recordPayment.mutateAsync({
        loanId,
        amount: Number(amount),
        method,
        reference: reference || undefined,
        notes: notes || undefined,
        receivedDate,
      });

      setShowForm(false);
      setAmount('');
      setReference('');
      setNotes('');
    } catch {
      // The mutation handles error presentation; keep the form open.
    }
  }

  return (
    <div className="space-y-4">
      {/* Outstanding Summary */}
      {outstanding && (
        <div className="grid grid-cols-4 gap-3">
          <SummaryCard
            icon={<DollarSign className="w-4 h-4 text-indigo-500" />}
            label="Total Outstanding"
            value={fmt(outstanding.totalOutstanding)}
            highlight={outstanding.totalOutstanding > 0}
          />
          <SummaryCard
            icon={<CheckCircle2 className="w-4 h-4 text-green-500" />}
            label="Total Paid"
            value={fmt(outstanding.totalPaid)}
          />
          <SummaryCard
            icon={<Clock className="w-4 h-4 text-amber-500" />}
            label="Next Due"
            value={outstanding.nextDueDate ? fmt(outstanding.nextDueAmount) : 'N/A'}
            sub={
              outstanding.nextDueDate
                ? new Date(outstanding.nextDueDate).toLocaleDateString('en-AU', {
                    month: 'short',
                    day: 'numeric',
                  })
                : undefined
            }
          />
          <SummaryCard
            icon={<AlertTriangle className="w-4 h-4 text-red-500" />}
            label="Overdue"
            value={outstanding.overdueCount > 0 ? fmt(outstanding.overdueAmount) : '$0.00'}
            sub={outstanding.overdueCount > 0 ? `${outstanding.overdueCount} payments` : undefined}
            highlight={outstanding.overdueCount > 0}
          />
        </div>
      )}

      {/* Progress bar */}
      {outstanding && outstanding.scheduleProgress.total > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600">Repayment Progress</span>
            <span className="text-xs text-gray-400">
              {outstanding.scheduleProgress.paid} / {outstanding.scheduleProgress.total} payments
            </span>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
            {outstanding.scheduleProgress.paid > 0 && (
              <div
                className="bg-green-500"
                style={{
                  width: `${(outstanding.scheduleProgress.paid / outstanding.scheduleProgress.total) * 100}%`,
                }}
              />
            )}
            {outstanding.scheduleProgress.partial > 0 && (
              <div
                className="bg-amber-400"
                style={{
                  width: `${(outstanding.scheduleProgress.partial / outstanding.scheduleProgress.total) * 100}%`,
                }}
              />
            )}
            {outstanding.scheduleProgress.overdue > 0 && (
              <div
                className="bg-red-400"
                style={{
                  width: `${(outstanding.scheduleProgress.overdue / outstanding.scheduleProgress.total) * 100}%`,
                }}
              />
            )}
          </div>
          <div className="flex gap-4 mt-2 text-[10px] text-gray-400">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500" /> Paid (
              {outstanding.scheduleProgress.paid})
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-400" /> Partial (
              {outstanding.scheduleProgress.partial})
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-400" /> Overdue (
              {outstanding.scheduleProgress.overdue})
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-gray-200" /> Pending (
              {outstanding.scheduleProgress.pending})
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Form */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Payment History</h3>
        {canRecordPayment && !showForm && (
          <Button
            variant="primary"
            onClick={() => setShowForm(true)}
            className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white text-sm font-medium rounded-lg hover: transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Record Payment
          </Button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Record Payment</h4>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {recordPayment.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {getMutationErrorMessage(recordPayment.error)}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  min={0.01}
                  step={0.01}
                  required
                  placeholder="0.00"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Payment Method *
              </label>
              <select
                value={method}
                onChange={e => setMethod(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reference #</label>
              <input
                type="text"
                value={reference}
                onChange={e => setReference(e.target.value)}
                placeholder="e.g. CHK-12345"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Received Date</label>
              <input
                type="date"
                value={receivedDate}
                onChange={e => setReceivedDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowForm(false)}
              className="w-fit"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={recordPayment.isPending}
              className="w-fit px-4 py-2 text-sm font-medium text-white rounded-lg hover: disabled:opacity-50 transition-colors"
            >
              {recordPayment.isPending ? 'Processing...' : 'Record Payment'}
            </Button>
          </div>
        </form>
      )}

      {/* Payment History Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loadingHistory ? (
          <div className="p-12 text-center text-sm text-gray-400">Loading payments...</div>
        ) : !Array.isArray(payments) || payments.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No payments recorded yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                {['Date', 'Amount', 'Allocation', 'Method', 'Reference', 'Status'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-500 px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {payments.map(p => (
                <tr key={p.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 text-sm text-gray-700">
                    {new Date(p.receivedDate || p.createdAt).toLocaleDateString('en-AU', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-semibold text-gray-900">
                    {fmt(p.amount)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs text-indigo-700 font-medium">
                      {(
                        p.allocations ??
                        (p.allocatedTo ? [{ indicator: p.allocatedTo, amount: p.amount }] : [])
                      )
                        .map(allocation => `${allocation.indicator}: ${fmt(allocation.amount)}`)
                        .join(' · ') || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">
                    {p.method.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 font-mono text-xs">
                    {p.reference || '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.status === 'APPLIED'
                          ? 'bg-green-50 text-green-700'
                          : p.status === 'VOID'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  sub,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${highlight ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-white'}`}
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className={`text-lg font-bold ${highlight ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}
